import React, { useState, useRef, useEffect, JSX } from 'react';
import { Toast } from 'primereact/toast';
import { userProfileStore } from '../stateManagers/stores/userProfileStore';

interface TopBarProps {
  title?: string;
  subtitle?: string;
  onExport?: () => void;
  onSettings?: () => void;
}

// ── Inline SVG icons (same approach as Sidebar — no icon-font dependency) ────
const Ico = ({ d, size = 15, style = {} }: { d: string | string[]; size?: number; style?: React.CSSProperties }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
type IcoProps = { size?: number; style?: React.CSSProperties };
const IcoCog = (p: IcoProps) => (
  <Ico
    {...p}
    d={[
      'M12 15a3 3 0 100-6 3 3 0 000 6z',
      'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
    ]}
  />
);
const IcoBell = (p: IcoProps) => (
  <Ico {...p} d={['M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 01-3.46 0']} />
);
const IcoDownload = (p: IcoProps) => (
  <Ico {...p} d={['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3']} />
);
const IcoUser = (p: IcoProps) => (
  <Ico {...p} d={['M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', 'M12 11a4 4 0 100-8 4 4 0 000 8z']} />
);
const IcoCalendar = (p: IcoProps) => (
  <Ico {...p} d={['M8 2v4M16 2v4M3 10h18', 'M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z']} />
);
const IcoBuilding = (p: IcoProps) => (
  <Ico
    {...p}
    d={[
      'M6 22V4a1 1 0 011-1h10a1 1 0 011 1v18',
      'M6 22h12',
      'M9 6h1M14 6h1M9 10h1M14 10h1M9 14h1M14 14h1',
      'M10 22v-4h4v4',
    ]}
  />
);
const NOTIF_ICON: Record<string, (p: IcoProps) => JSX.Element> = {
  'pi-user': IcoUser,
  'pi-calendar': IcoCalendar,
  'pi-bell': IcoBell,
};

const getInitials = (name?: string | null) => {
  if (!name) return 'U';
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

const TopBar: React.FC<TopBarProps> = ({ title = 'Dashboard', subtitle, onExport, onSettings }) => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [agencyTipOpen, setAgencyTipOpen] = useState(false);
  const toast = useRef<Toast>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const { profile, error, fetchProfile, clearError } = userProfileStore();

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (error && toast.current) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: error, life: 3000 });
      clearError();
    }
  }, [error, clearError]);

  // Close notifications on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotificationsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const firstName = profile?.name?.trim().split(' ')[0];
  const subText = subtitle ?? (firstName ? `Welcome back ${firstName}` : 'Welcome back');
  const agency = (profile as any)?.agency;

  const notifications = [
    { icon: 'pi-user', text: 'New student registration', time: '5 min ago' },
    { icon: 'pi-calendar', text: 'Exam scheduled for tomorrow', time: '1 hour ago' },
    { icon: 'pi-bell', text: 'Fee payment reminder', time: '2 hours ago' },
  ];

  return (
    <>
      <Toast ref={toast} />

      <div
        style={{
          height: '68px',
          background: '#ffffff',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 28px',
          gap: '22px',
          position: 'relative',
          zIndex: 50,
        }}
      >
        {/* Left - Title */}
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              margin: 0,
              fontSize: '1.13rem',
              fontWeight: 700,
              color: '#0f2f2b',
              lineHeight: 1.25,
              letterSpacing: '-0.2px',
            }}
          >
            {title}
          </h1>
          <div style={{ fontSize: '0.77rem', color: '#6b7280', marginTop: '2px' }}>{subText}</div>
        </div>

        {/* Right - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          {/* Settings */}
          <button className="tb-icon-btn" onClick={onSettings} aria-label="Settings">
            <IcoCog size={15} />
          </button>

          {/* Notifications */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button
              className="tb-icon-btn"
              onClick={() => setNotificationsOpen(o => !o)}
              aria-label="Notifications"
            >
              <IcoBell size={15} />
              <span
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '9px',
                  width: '7px',
                  height: '7px',
                  background: '#ef4444',
                  borderRadius: '50%',
                  border: '2px solid #fff',
                }}
              />
            </button>

            {notificationsOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '45px',
                  right: 0,
                  width: '297px',
                  background: '#fff',
                  borderRadius: '13px',
                  boxShadow: '0 12px 36px rgba(15,47,43,0.16)',
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden',
                  animation: 'tbSlideDown 0.2s ease',
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    padding: '13px 16px',
                    borderBottom: '1px solid #EEF0F2',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: '0.855rem', fontWeight: 600, color: '#0f2f2b' }}>Notifications</h3>
                  <span
                    style={{
                      background: '#e7f6ec',
                      color: '#115550',
                      fontSize: '0.63rem',
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: '20px',
                      border: '1px solid #c5e8d1',
                    }}
                  >
                    3 New
                  </span>
                </div>
                <div style={{ maxHeight: '270px', overflowY: 'auto' }}>
                  {notifications.map((n, idx) => {
                    const NotifIcon = NOTIF_ICON[n.icon] || IcoBell;
                    return (
                      <div
                        key={idx}
                        className="tb-notif"
                        style={{
                          padding: '12px 16px',
                          borderBottom: idx < notifications.length - 1 ? '1px solid #F3F4F6' : 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          gap: '11px',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            width: '31px',
                            height: '31px',
                            borderRadius: '8px',
                            background: '#eef7f1',
                            border: '1px solid #d7ecdd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <NotifIcon size={14} style={{ color: '#115550' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 3px', fontSize: '0.77rem', color: '#0f2f2b', fontWeight: 500 }}>{n.text}</p>
                          <span style={{ fontSize: '0.67rem', color: '#9ca3af' }}>{n.time}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ padding: '9px 16px', borderTop: '1px solid #EEF0F2', textAlign: 'center' }}>
                  <button
                    className="tb-link"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: '4px',
                    }}
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ width: '1px', height: '23px', background: '#E5E7EB', margin: '0 4px' }} />

          {/* User avatar — hover shows agency name + code */}
          {agency && (
            <div
              className="tb-avatar-wrap"
              onMouseEnter={() => setAgencyTipOpen(true)}
              onMouseLeave={() => setAgencyTipOpen(false)}
            >
              <div className="tb-avatar">{getInitials(profile?.name)}</div>

              {agencyTipOpen && (
                <div className="tb-agency-tip">
                  <div className="tb-agency-tip-icon">
                    <IcoBuilding size={14} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="tb-agency-name">{agency.name}</div>
                    <div className="tb-agency-code">{agency.agency_code}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Export */}
          {onExport && (
            <button className="tb-export" onClick={onExport}>
              Export
              <IcoDownload size={13} />
            </button>
          )}
        </div>
      </div>

      <style>{`
        .tb-icon-btn {
          position: relative;
          width: 36px;
          height: 36px;
          background: #fff;
          border: 1px solid #E5E7EB;
          border-radius: 9px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #374151;
          transition: all 0.2s ease;
        }
        .tb-icon-btn:hover {
          background: #f1f7f4;
          border-color: #115550;
          color: #115550;
        }
        .tb-notif:hover { background: #F7FAF9; }
        .tb-link { color: #115550; transition: color 0.2s ease; }
        .tb-link:hover { color: #0b3a37; text-decoration: underline; }
        .tb-export {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 0 18px;
          height: 38px;
          background: #115550;
          color: #fff;
          border: 1px solid #0b3a37;
          border-radius: 9px;
          font-size: 0.81rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(17,85,80,0.25);
          transition: all 0.2s ease;
        }
        .tb-export:hover { background: #0b3a37; transform: translateY(-1px); }

        .tb-avatar-wrap { position: relative; }
        .tb-avatar {
          width: 36px;
          height: 36px;
          border-radius: 9px;
          background: #c8e94b;
          color: #0f4a43;
          font-weight: 700;
          font-size: 0.77rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: default;
          border: 1px solid #b6d63f;
        }
        .tb-agency-tip {
          position: absolute;
          top: 45px;
          right: 0;
          min-width: 180px;
          background: #0f2f2b;
          color: #fff;
          border-radius: 11px;
          padding: 11px 13px;
          display: flex;
          align-items: center;
          gap: 9px;
          box-shadow: 0 12px 32px rgba(15,47,43,0.28);
          animation: tbSlideDown 0.15s ease;
          z-index: 1000;
        }
        .tb-agency-tip-icon {
          width: 29px;
          height: 29px;
          border-radius: 7px;
          background: rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #c8e94b;
        }
        .tb-agency-name {
          font-size: 0.77rem;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .tb-agency-code {
          font-size: 0.67rem;
          color: rgba(255,255,255,0.6);
          margin-top: 1px;
        }

        @keyframes tbSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default TopBar;